import { useState } from 'react';
import { Link } from 'react-router-dom';

const faqs = [
  {
    q: 'How long does a custom suit take?',
    a: 'A bespoke custom suit typically takes 4 to 6 weeks from your initial consultation to final delivery. This timeline includes fabric selection, precise measuring, hand construction, and your fitting appointments. Rush services may be available depending on scheduling — please call us to discuss your timeline.',
  },
  {
    q: 'How much does custom tailoring cost?',
    a: 'Custom suit pricing varies based on fabric selection and construction details, but generally starts from a few hundred dollars and scales with the quality of materials chosen. Custom shirts start at lower price points. We believe exceptional craftsmanship is accessible — contact us for a personalized quote based on your specific needs.',
  },
  {
    q: 'Do you offer fittings?',
    a: 'Absolutely. Multiple fitting appointments are a standard part of our process. We do not deliver a garment until the fit, drape, and finish meet our exacting standards. We also offer house and office call fittings throughout Massachusetts — we come to you.',
  },
  {
    q: 'Do you travel to clients for measurements?',
    a: 'Yes. We make house and office calls throughout Massachusetts and New England. Simply call us to arrange an appointment at your location. Our master tailor will bring fabric samples, take your measurements, and guide you through the entire selection process — all from the comfort of your home or office.',
  },
  {
    q: 'What fabrics do you carry for custom suits?',
    a: 'We carry over 5,000 premium fabric options including Italian wools from Loro Piana and Ermenegildo Zegna, English tweeds, cashmere blends, seasonal linens, and performance blends. Our selection covers every occasion from boardroom to black tie.',
  },
];

const ServicesFAQSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <>
      {/* FAQ */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-[#C4A962] font-semibold tracking-widest uppercase text-sm mb-3">
              Quick Answers
            </p>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-500 text-lg">
              Everything you need to know about our custom tailoring services.
            </p>
          </div>

          {/* Schema-ready FAQ structured markup */}
          <div
            itemScope
            itemType="https://schema.org/FAQPage"
            className="space-y-4"
          >
            {faqs.map((faq, i) => (
              <div
                key={i}
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <span itemProp="name" className="font-semibold text-gray-900 pr-4">
                    {faq.q}
                  </span>
                  <i
                    className={`text-[#C4A962] text-xl flex-shrink-0 transition-transform ${
                      openIndex === i ? 'ri-subtract-line' : 'ri-add-line'
                    }`}
                  ></i>
                </button>
                {openIndex === i && (
                  <div
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                    className="px-6 pb-5"
                  >
                    <p itemProp="text" className="text-gray-600 leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#C4A962]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Get Dressed for Success?
          </h2>
          <p className="text-white/85 text-xl mb-10 max-w-2xl mx-auto leading-relaxed">
            Your perfect suit, shirt, or garment is waiting. Call for an appointment
            or book a consultation today — in-store or at your location.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-[#C4A962] px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 transition-all transform hover:scale-105 whitespace-nowrap"
            >
              <i className="ri-calendar-line mr-2"></i>
              Book a Consultation
            </Link>
            <a
              href="tel:781-237-3166"
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-white/10 transition-all whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              Call 781-237-3166
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default ServicesFAQSection;
